import React, { forwardRef } from 'react';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'ghost' | 'subtle' | 'solid';

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  label: string; // for aria-label
}

const sizeMap: Record<IconButtonSize, number> = { sm: 28, md: 32, lg: 40 };

const variantMap: Record<IconButtonVariant, React.CSSProperties> = {
  ghost: {
    background: 'transparent',
    color: 'var(--fg-2)',
    border: '1px solid transparent',
  },
  subtle: {
    background: 'var(--cg-neutral-50)',
    color: 'var(--fg-1)',
    border: '1px solid var(--border-1)',
  },
  solid: {
    background: 'var(--accent)',
    color: '#fff',
    border: '1px solid var(--accent)',
  },
};

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { size = 'md', variant = 'ghost', label, style, children, type = 'button', ...rest },
  ref,
) {
  const dim = sizeMap[size];
  const base: React.CSSProperties = {
    width: dim,
    height: dim,
    borderRadius: 'var(--radius-md)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: size === 'lg' ? 18 : size === 'sm' ? 13 : 15,
    fontFamily: 'var(--font-sans)',
    transition: 'background 220ms var(--ease-standard), color 220ms var(--ease-standard)',
    flexShrink: 0,
    ...variantMap[variant],
    ...style,
  };

  return (
    <button ref={ref} type={type} aria-label={label} title={label} style={base} {...rest}>
      {children}
    </button>
  );
});

export default IconButton;
